import { API_BASE_URL } from "./api-base";

// Storage key for the magic-link / OAuth *session* token (NOT the break-glass
// ADMIN_BEARER_TOKEN, which the UI never accepts — see staff-access.md). The
// name is deliberately not "bearer" to avoid implying the env-bearer lives
// here. Renaming this key logs existing sessions out, which is acceptable: the
// trade-off (session token in localStorage, XSS-readable) is owned in
// docs/runbooks/staff-access.md § "Session token storage".
const TOKEN_KEY = "leadrails_session_token";
const ACTING_AS_KEY = "acting_as_client_id";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export function isAuthenticated(): boolean {
  return getToken() !== null;
}

export function getActingAsClientId(): string | null {
  return localStorage.getItem(ACTING_AS_KEY);
}

export function setActingAsClientId(id: string): void {
  localStorage.setItem(ACTING_AS_KEY, id);
}

export function clearActingAsClientId(): void {
  localStorage.removeItem(ACTING_AS_KEY);
}

// AdminAuth principal mirrors the worker's AdminAuth union. Result of GET
// /admin/auth/me.
export type AgencyUserPrincipal = {
  kind: "agency_user";
  user_id: string;
  agency_id: string;
  accessible_client_ids: string[];
  acting_as_client_id: string;
  email: string;
};

// Staff is unscoped by default. Two ways to land here:
//   - `via: "magic_link"` — signed in through the normal email-link flow
//     with an email listed in INTERNAL_STAFF_EMAILS on the worker.
//     `user_id` + `email` are populated; audit attribution is per-user.
//   - `via: "bearer"` — break-glass via the env-bearer ADMIN_BEARER_TOKEN.
//     `user_id` + `email` are null. Reserved for recovery when magic-link
//     auth is unavailable.
//
// Optional `acting_as_client_id` is populated by the worker when the staff
// session sent an `X-LR-Acting-As` header — this is the "View as workspace"
// support affordance, used by LeadRails staff to debug a specific customer
// workspace. The field mirrors the worker's AdminAuth.staff variant;
// agency_user uses the same header for its always-on acting-as semantics.
export type StaffPrincipal = {
  kind: "staff";
  user_id: string | null;
  email: string | null;
  via: "bearer" | "magic_link";
  acting_as_client_id?: string;
};

export type AdminAuthPrincipal =
  | StaffPrincipal
  | { kind: "client_user"; client_id: string; user_id: string; email: string }
  | AgencyUserPrincipal;

export interface MeResponse {
  ok: true;
  auth: AdminAuthPrincipal;
}

export async function fetchMe(): Promise<AdminAuthPrincipal | null> {
  const token = getToken();
  if (!token) return null;
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
  // Forward the acting-as client choice so /admin/auth/me reflects what the
  // user picked in the sidebar's ClientPicker (otherwise the worker defaults
  // to the first accessible client and the picker selection looks ignored).
  const actingAs = getActingAsClientId();
  if (actingAs) headers["X-LR-Acting-As"] = actingAs;
  // Raw fetch intentional: authedFetch imports from this module, so using it
  // here would create a circular dependency. The necessary auth headers are
  // built manually above, and 401 is handled below via clearToken().
  const resp = await fetch(`${API_BASE_URL}/admin/auth/me`, { headers });
  if (!resp.ok) {
    if (resp.status === 401) clearToken();
    return null;
  }
  const body = (await resp.json()) as MeResponse;
  return body.auth;
}

export async function requestMagicLink(email: string): Promise<{ ok: boolean }> {
  const resp = await fetch(`${API_BASE_URL}/admin/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  return { ok: resp.ok };
}

export async function verifyMagicLink(
  token: string,
): Promise<{ ok: true; session_token: string; expires_at: string } | { ok: false; error: string }> {
  const resp = await fetch(`${API_BASE_URL}/admin/auth/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  const body = (await resp.json()) as
    | { ok: true; session_token: string; expires_at: string }
    | { ok: false; error: string };
  return body;
}

export async function logout(): Promise<void> {
  const token = getToken();
  if (!token) return;
  try {
    await fetch(`${API_BASE_URL}/admin/auth/logout`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
  } finally {
    clearToken();
  }
}

// OAuth SSO (M14) — initiates the Authorization Code + PKCE flow by asking
// the API for an authorization URL, then redirecting the browser there.
export async function startOAuth(provider: "google" | "microsoft"): Promise<void> {
  const resp = await fetch(`${API_BASE_URL}/admin/auth/oauth/${provider}/start`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  if (!resp.ok) {
    throw new Error(`oauth_start_failed: ${resp.status}`);
  }
  const body = (await resp.json()) as { ok: true; authorization_url: string } | { ok: false; error: string };
  if (!body.ok) {
    throw new Error(`oauth_start_error: ${"error" in body ? body.error : "unknown"}`);
  }
  window.location.href = body.authorization_url;
}
